import React, { useCallback, useContext } from 'react';
import classNames from 'classnames';
import useMatchMedia from 'hooks/useMatchMedia';
import {
  DataRoomMember,
  DataRoomMemberRole,
} from '../../models/DataRoomMember';
import { useTranslation } from 'react-i18next';
import { capitalizeName } from 'utils';
import { NewTag } from '../NewTag';
import RoleTag from '../../subrouters/components/RoleTag';
import { Button } from 'ncoded-component-library';
import { isDataRoomOwner } from '../../utils';
import SettingsIcon from 'icons/Settings.icon';
import DataRoomContext from '../../providers/DataRoom/DataRoom.context';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import LeadTag from '../LeadTag';
import MemberRoleInstantChange from './MemberRoleInstantChange';

import './DataRoomMembersTable.styles.scss';
import './DataRoomMembersTable.styles.responsive.scss';

type DataRoomMembersTableProps = {
  className?: string;
  members: DataRoomMember[];
  isBuySide?: boolean;
  onEdit: (member: DataRoomMember) => void;
  onEditMemberRole?: (
    memberId: string,
    updated: Partial<DataRoomMember>,
  ) => void;
};

const DataRoomMembersTable: React.FC<DataRoomMembersTableProps> = (props) => {
  const {
    className,
    isBuySide = false,
    members = [],
    onEdit,
    onEditMemberRole,
  } = props;

  const { currentUser } = useContext(CurrentUserContext);
  const { doesntHaveAccess } = useContext(DataRoomContext);

  const classes = classNames('data-room-members-table', className);

  const isTablet = useMatchMedia('isTablet');

  const { t } = useTranslation();

  const getCanEditMember = useCallback(
    (role: DataRoomMemberRole, userId: string) => {
      const isOwner = isDataRoomOwner(role);
      const isCurrentUser = userId === currentUser._id;

      if (doesntHaveAccess || isOwner || isCurrentUser) return false;

      return true;
    },
    [currentUser, doesntHaveAccess],
  );

  const renderName = (
    isAccepted: boolean,
    isRequested: boolean,
    email: string,
    fullName: string,
    requestedDataRoomAccessAt: Date,
    isLead: boolean,
  ) => (
    <div className="name">
      <span className={classNames({ invited: !isAccepted || isRequested })}>
        {!isAccepted ? t('invited') : capitalizeName(fullName)}

        {isLead && <LeadTag />}

        {isRequested && (
          <NewTag
            variant="green"
            daysAgo={3}
            time={requestedDataRoomAccessAt}
          />
        )}
      </span>

      {email && <p className="email">{email}</p>}
    </div>
  );

  const renderTeams = useCallback(
    (teams: string[]) => (
      <div className="teams">
        {teams.map((team) => (
          <RoleTag key={team}>{team}</RoleTag>
        ))}
      </div>
    ),
    [],
  );

  const renderRole = useCallback(
    ({ _id, role, qaRole, userId }: DataRoomMember, isQaRole = false) => (
      <MemberRoleInstantChange
        memberId={_id}
        isBuySide={isBuySide}
        {...(isQaRole
          ? {
              qaRole,
            }
          : { role })}
        {...(getCanEditMember(role, userId) && { onChange: onEditMemberRole })}
      />
    ),
    [isBuySide, getCanEditMember, onEditMemberRole],
  );

  const renderEditButton = useCallback(
    (member: DataRoomMember) => {
      if (!getCanEditMember(member.role, member.userId)) return null;

      return (
        <Button
          variant="icon"
          icon={SettingsIcon}
          onClick={() => onEdit(member)}
        />
      );
    },
    [getCanEditMember, onEdit],
  );

  return isTablet ? (
    <div className={classes}>
      {members.map((member) => {
        const {
          _id,
          isAccepted,
          requestedDataRoomAccess,
          email,
          fullName,
          teams,
          isLead,
          requestedDataRoomAccessAt,
        } = member;

        return (
          <div key={_id} className="data-room-members-table__mobile-card">
            <div>
              {renderName(
                isAccepted,
                requestedDataRoomAccess,
                email,
                fullName,
                requestedDataRoomAccessAt,
                isLead,
              )}
              <div className="actions">{renderEditButton(member)}</div>
            </div>
            <div className="tags">
              {renderRole(member)}
              {renderRole(member, true)}
              {renderTeams(teams)}
            </div>
          </div>
        );
      })}
    </div>
  ) : (
    <table className={classes}>
      <thead>
        <tr className="header">
          <th>{t('member')}</th>
          <th>{t('files')}</th>
          <th>{t('q&a')}</th>
          <th>{t('role')}</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {members.map((member) => {
          const {
            _id,
            isAccepted,
            requestedDataRoomAccess,
            email,
            fullName,
            teams,
            isLead,
            requestedDataRoomAccessAt,
          } = member;

          return (
            <tr key={_id}>
              <td>
                {renderName(
                  isAccepted,
                  requestedDataRoomAccess,
                  email,
                  fullName,
                  requestedDataRoomAccessAt,
                  isLead,
                )}
              </td>
              <td>{renderRole(member)}</td>
              <td>{renderRole(member, true)}</td>
              <td>{renderTeams(teams)}</td>
              <td>
                <div className="actions">{renderEditButton(member)}</div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default DataRoomMembersTable;
